import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  completeReminder,
  snoozeReminder,
  dismissAlertModal,
  dismissReminder,
  dismissAllReminders,
  markReminderShown,
} from '../../Redux/Slice/ReminderSlice';
import SnoozeModal from './SnoozeModal';
import { toast } from 'react-toastify';
import {
  Bell,
  Clock,
  CheckCircle,
  ExternalLink,
  PhoneCall,
  X,
  AlertTriangle,
  Calendar,
  Sparkles,
  Tag,
  FileText,
  User,
  ChevronLeft,
  ChevronRight,
  Flame,
  List,
  LayoutGrid,
} from 'lucide-react';
import moment from 'moment';
import Cookies from 'js-cookie';
import './ReminderAlertModal.css';

// Play a pleasant 3-tone reminder chime using Web Audio API
function playReminderChime() {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();

    const playTone = (freq, start, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
      gain.gain.setValueAtTime(0.25, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + duration);
    };

    // Elegant 3-tone chime
    playTone(659.25, 0, 0.3);
    playTone(830.61, 0.15, 0.3);
    playTone(987.77, 0.3, 0.5);
  } catch (e) {
    console.warn('Could not play reminder chime:', e);
  }
}

const ReminderAlertModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { activeAlerts } = useSelector((state) => state.reminder);
  const { LoginData } = useSelector((state) => state.auth);
  const cookieUser =
    Cookies.get('IDQUA') ||
    (Cookies.get('loginData') ? JSON.parse(Cookies.get('loginData'))?.data?._id : null);
  const currentUserId = LoginData?.data?._id || cookieUser;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'list'
  const [snoozeReminderItem, setSnoozeReminderItem] = useState(null);
  const [showSnoozeModal, setShowSnoozeModal] = useState(false);
  const [loadingCompleteId, setLoadingCompleteId] = useState(null);
  const [loadingSnoozeMinutes, setLoadingSnoozeMinutes] = useState(null);

  // Ensure index is within bounds before effects
  const safeIndex =
    activeAlerts && activeAlerts.length > 0
      ? currentIndex < activeAlerts.length
        ? currentIndex
        : Math.max(0, activeAlerts.length - 1)
      : 0;
  const currentAlert = activeAlerts && activeAlerts.length > 0 ? (activeAlerts[safeIndex] || activeAlerts[0]) : null;
  const currentAlertId = currentAlert?._id;

  // When a new active alert arrives, play chime
  useEffect(() => {
    if (activeAlerts && activeAlerts.length > 0) {
      playReminderChime();
    }
  }, [activeAlerts?.length]);

  // Mark alert as shown in user account
  useEffect(() => {
    if (currentAlertId && currentUserId) {
      dispatch(markReminderShown({ id: currentAlertId, userId: currentUserId }));
    }
  }, [currentAlertId, currentUserId, dispatch]);

  // Keyboard navigation for multiple alerts (ArrowLeft / ArrowRight)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!activeAlerts || activeAlerts.length <= 1) return;
      if (e.key === 'ArrowLeft') {
        setCurrentIndex((prev) => Math.max(0, prev - 1));
      } else if (e.key === 'ArrowRight') {
        setCurrentIndex((prev) => Math.min(activeAlerts.length - 1, prev + 1));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeAlerts?.length]);

  if (!activeAlerts || activeAlerts.length === 0 || !currentAlert) {
    return null;
  }

  const handleComplete = async (reminder) => {
    setLoadingCompleteId(reminder._id);
    try {
      await dispatch(
        completeReminder({
          id: reminder._id,
          userId: currentUserId,
        })
      ).unwrap();
      dispatch(dismissAlertModal(reminder._id));
      toast.success('Reminder marked as completed!');
      if (currentIndex > 0) {
        setCurrentIndex((prev) => prev - 1);
      }
    } catch (err) {
      toast.error(err || 'Failed to complete reminder.');
    } finally {
      setLoadingCompleteId(null);
    }
  };

  const handleQuickSnooze = async (reminder, minutes) => {
    setLoadingSnoozeMinutes(minutes);
    try {
      await dispatch(
        snoozeReminder({
          id: reminder._id,
          minutes,
          userId: currentUserId,
        })
      ).unwrap();
      toast.success(`Reminder snoozed for ${minutes} minutes`);
      if (currentIndex > 0) {
        setCurrentIndex((prev) => prev - 1);
      }
    } catch (err) {
      toast.error(err || 'Failed to snooze reminder.');
    } finally {
      setLoadingSnoozeMinutes(null);
    }
  };

  const handleOpenSnooze = (reminder) => {
    setSnoozeReminderItem(reminder);
    setShowSnoozeModal(true);
  };

  const handleDismiss = async (reminder) => {
    dispatch(dismissAlertModal(reminder._id));
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
    try {
      await dispatch(dismissReminder({ id: reminder._id, userId: currentUserId })).unwrap();
    } catch (err) {
      console.error('Error dismissing reminder on server:', err);
    }
  };

  const handleDismissAll = async () => {
    if (!activeAlerts || activeAlerts.length === 0) return;
    const ids = activeAlerts.map((a) => a._id);
    try {
      await dispatch(dismissAllReminders({ reminderIds: ids, userId: currentUserId })).unwrap();
      toast.info(`Dismissed all ${ids.length} reminders`);
      setCurrentIndex(0);
    } catch (err) {
      console.error('Error dismissing all reminders on server:', err);
    }
  };

  const handleViewCallDetails = (reminder) => {
    const complaintId =
      typeof reminder.complaintId === 'object' && reminder.complaintId !== null
        ? reminder.complaintId._id
        : typeof reminder.complaintId === 'string'
          ? reminder.complaintId
          : '';

    if (complaintId) {
      dispatch(dismissAlertModal(reminder._id));
      navigate(`/ComplainDetailsIdSupport/${complaintId}`);
    } else if (reminder.callNo) {
      toast.info(`Linked Call #${reminder.callNo}`);
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'Urgent':
        return {
          label: 'Urgent Priority',
          bg: '#fef2f2',
          color: '#dc2626',
          border: '#fecaca',
          gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          iconColor: '#ef4444',
          shadow: '0 4px 14px rgba(239, 68, 68, 0.3)',
          Icon: Flame,
        };
      case 'High':
        return {
          label: 'High Priority',
          bg: '#fffbeb',
          color: '#d97706',
          border: '#fde68a',
          gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          iconColor: '#f59e0b',
          shadow: '0 4px 14px rgba(245, 158, 11, 0.3)',
          Icon: AlertTriangle,
        };
      case 'Low':
        return {
          label: 'Low Priority',
          bg: '#ecfdf5',
          color: '#059669',
          border: '#a7f3d0',
          gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          iconColor: '#10b981',
          shadow: '0 4px 14px rgba(16, 185, 129, 0.3)',
          Icon: CheckCircle,
        };
      default:
        return {
          label: 'Medium Priority',
          bg: '#eff6ff',
          color: '#2563eb',
          border: '#bfdbfe',
          gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          iconColor: '#3b82f6',
          shadow: '0 4px 14px rgba(37, 99, 235, 0.3)',
          Icon: Clock,
        };
    }
  };

  const pConfig = getPriorityBadge(currentAlert.priority);
  const PriorityIcon = pConfig.Icon;

  // Format date and time friendly
  const formatFriendlyDateTime = () => {
    if (!currentAlert.reminderDate || !currentAlert.reminderTime) {
      return { time: '--:--', date: 'Today', isOverdue: false, overdueText: '' };
    }
    try {
      const m = moment(`${currentAlert.reminderDate} ${currentAlert.reminderTime}`, 'YYYY-MM-DD HH:mm');
      const isToday = m.isSame(moment(), 'day');
      const isTomorrow = m.isSame(moment().add(1, 'day'), 'day');
      const dayText = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : m.format('DD MMM YYYY');
      const isPast = m.isBefore(moment().subtract(1, 'minute'));
      const overdueText = isPast ? m.fromNow() : '';

      return {
        time: m.format('hh:mm A'),
        date: dayText,
        isOverdue: isPast,
        overdueText,
      };
    } catch (e) {
      return {
        time: currentAlert.reminderTime,
        date: currentAlert.reminderDate,
        isOverdue: false,
        overdueText: '',
      };
    }
  };

  const timeData = formatFriendlyDateTime();
  const hasCallLink = Boolean(currentAlert.callNo || currentAlert.complaintId);

  return (
    <>
      <div className="modal fade show d-block reminder-alert-backdrop" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered reminder-alert-dialog">
          <div className="modal-content reminder-alert-content">
            {/* Top Glowing Gradient Line */}
            <div
              className="reminder-accent-bar"
              style={{ background: pConfig.gradient }}
            />

            {/* Modal Header */}
            <div className="px-4 pt-4 pb-2 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-3">
                {/* Glowing Radar Bell Icon */}
                <div
                  className="reminder-bell-wrapper"
                  style={{
                    background: pConfig.bg,
                    border: `1.5px solid ${pConfig.border}`,
                    boxShadow: pConfig.shadow,
                  }}
                >
                  <Bell size={22} style={{ color: pConfig.iconColor }} />
                  <span
                    className="position-absolute top-0 end-0 translate-middle p-1.5 rounded-circle border border-white"
                    style={{ backgroundColor: pConfig.iconColor }}
                  />
                </div>

                <div>
                  <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                    {/* Live Due / Overdue Dot */}
                    <span className={`reminder-live-badge ${timeData.isOverdue ? 'reminder-overdue-badge' : ''}`}>
                      <span className="reminder-pulse-dot" />
                      {timeData.isOverdue ? `Overdue (${timeData.overdueText})` : 'Due Now'}
                    </span>

                    {/* Multiple Alerts Navigation */}
                    {activeAlerts.length > 1 && (
                      <span className="reminder-pager-pill">
                        <button
                          type="button"
                          className="reminder-pager-btn"
                          disabled={safeIndex === 0}
                          onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
                          title="Previous reminder (or Left Arrow)"
                        >
                          <ChevronLeft size={13} />
                        </button>
                        <span>{safeIndex + 1} of {activeAlerts.length}</span>
                        <button
                          type="button"
                          className="reminder-pager-btn"
                          disabled={safeIndex === activeAlerts.length - 1}
                          onClick={() => setCurrentIndex((prev) => Math.min(activeAlerts.length - 1, prev + 1))}
                          title="Next reminder (or Right Arrow)"
                        >
                          <ChevronRight size={13} />
                        </button>
                      </span>
                    )}
                  </div>

                  <h5 className="fw-bold text-dark mb-0" style={{ fontSize: '16.5px', letterSpacing: '-0.01em' }}>
                    {activeAlerts.length > 1 ? `Reminder Notification (${safeIndex + 1}/${activeAlerts.length})` : 'Reminder Notification'}
                  </h5>
                </div>
              </div>

              {/* Close / Dismiss Button */}
              <button
                type="button"
                className="btn btn-sm btn-light rounded-circle p-1.5 border-0 text-secondary d-flex align-items-center justify-content-center"
                onClick={() => handleDismiss(currentAlert)}
                title="Dismiss (Esc)"
                style={{
                  width: '32px',
                  height: '32px',
                  backgroundColor: '#f1f5f9',
                  transition: 'all 0.15s ease',
                }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Multi-alert status bar & view toggle */}
            {activeAlerts.length > 1 && (
              <div
                className="px-4 py-2 border-bottom d-flex align-items-center justify-content-between"
                style={{ backgroundColor: '#f8fafc' }}
              >
                <div className="d-flex align-items-center gap-1.5 text-primary fw-semibold" style={{ fontSize: '12px' }}>
                  <Sparkles size={14} />
                  <span>{activeAlerts.length} Due Reminders Active</span>
                </div>
                <div className="btn-group btn-group-sm rounded-pill p-0.5 border bg-white shadow-xs">
                  <button
                    type="button"
                    className={`btn btn-xs rounded-pill px-2.5 py-0.5 border-0 d-inline-flex align-items-center gap-1 ${viewMode === 'card' ? 'btn-primary text-white fw-bold' : 'text-secondary'
                      }`}
                    style={{ fontSize: '11px' }}
                    onClick={() => setViewMode('card')}
                  >
                    <LayoutGrid size={11} />
                    <span>Card</span>
                  </button>
                  <button
                    type="button"
                    className={`btn btn-xs rounded-pill px-2.5 py-0.5 border-0 d-inline-flex align-items-center gap-1 ${viewMode === 'list' ? 'btn-primary text-white fw-bold' : 'text-secondary'
                      }`}
                    style={{ fontSize: '11px' }}
                    onClick={() => setViewMode('list')}
                  >
                    <List size={11} />
                    <span>View All ({activeAlerts.length})</span>
                  </button>
                </div>
              </div>
            )}

            {/* Modal Content Body */}
            <div className="px-4 py-3">
              {viewMode === 'list' && activeAlerts.length > 1 ? (
                /* Stacked List View of All Due Reminders */
                <div className="reminder-list-container">
                  {activeAlerts.map((item, idx) => {
                    const pConf = getPriorityBadge(item.priority);
                    const itemTime = (() => {
                      try {
                        const m = moment(`${item.reminderDate} ${item.reminderTime}`, 'YYYY-MM-DD HH:mm');
                        const isPast = m.isBefore(moment().subtract(1, 'minute'));
                        return {
                          time: m.format('hh:mm A'),
                          date: m.isSame(moment(), 'day') ? 'Today' : m.format('DD MMM'),
                          isOverdue: isPast,
                          overdueText: isPast ? m.fromNow() : '',
                        };
                      } catch (e) {
                        return { time: item.reminderTime, date: item.reminderDate, isOverdue: false, overdueText: '' };
                      }
                    })();

                    return (
                      <div
                        key={item._id || idx}
                        className="reminder-list-card-item"
                        style={{ borderLeft: `4px solid ${pConf.iconColor}` }}
                      >
                        {/* Top meta row */}
                        <div className="d-flex align-items-center justify-content-between mb-1.5 flex-wrap gap-1">
                          <div className="d-flex align-items-center gap-1.5 flex-wrap">
                            <span
                              className="badge rounded-pill"
                              style={{
                                backgroundColor: pConf.bg,
                                color: pConf.color,
                                border: `1px solid ${pConf.border}`,
                                fontSize: '10.5px',
                                fontWeight: '700',
                              }}
                            >
                              #{idx + 1} {pConf.label}
                            </span>
                            {item.reminderType && (
                              <span className="badge rounded-pill bg-light text-secondary border" style={{ fontSize: '10px' }}>
                                {item.reminderType}
                              </span>
                            )}
                            <span className={`reminder-live-badge ${itemTime.isOverdue ? 'reminder-overdue-badge' : ''}`} style={{ fontSize: '9.5px', padding: '1px 5px' }}>
                              {itemTime.isOverdue ? `Overdue (${itemTime.overdueText})` : 'Due Now'}
                            </span>
                          </div>

                          <div className="d-flex align-items-center gap-1 text-muted" style={{ fontSize: '11px' }}>
                            <Clock size={11} />
                            <strong className="text-dark">{itemTime.time}</strong>
                            <span>({itemTime.date})</span>
                          </div>
                        </div>

                        {/* Title & Description */}
                        <div className="fw-bold text-dark mb-1" style={{ fontSize: '14.5px', lineHeight: '1.3' }}>
                          {item.title}
                        </div>
                        {item.description && (
                          <div className="text-secondary mb-2" style={{ fontSize: '12px', lineHeight: '1.4' }}>
                            {item.description}
                          </div>
                        )}

                        {/* Token link if available */}
                        {item.callNo && (
                          <div className="mb-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-0 text-decoration-none text-primary fw-semibold d-inline-flex align-items-center gap-1"
                              style={{ fontSize: '11.5px' }}
                              onClick={() => handleViewCallDetails(item)}
                            >
                              <PhoneCall size={11} className="text-success" />
                              <span>Token #{item.callNo}</span>
                              <ExternalLink size={10} />
                            </button>
                          </div>
                        )}

                        {/* Actions row */}
                        <div className="d-flex align-items-center justify-content-between pt-2 border-top border-light gap-2">
                          <div className="d-flex align-items-center gap-1">
                            <button
                              type="button"
                              className="btn btn-xs btn-outline-secondary rounded-pill px-2 py-1"
                              style={{ fontSize: '11px' }}
                              onClick={() => handleQuickSnooze(item, 5)}
                              disabled={loadingSnoozeMinutes !== null}
                              title="Snooze 5 mins"
                            >
                              +5m
                            </button>
                            <button
                              type="button"
                              className="btn btn-xs btn-outline-secondary rounded-pill px-2 py-1"
                              style={{ fontSize: '11px' }}
                              onClick={() => handleQuickSnooze(item, 15)}
                              disabled={loadingSnoozeMinutes !== null}
                              title="Snooze 15 mins"
                            >
                              +15m
                            </button>
                            <button
                              type="button"
                              className="btn btn-xs btn-link text-muted p-1 text-decoration-none"
                              style={{ fontSize: '11px' }}
                              onClick={() => handleDismiss(item)}
                              title="Dismiss"
                            >
                              Dismiss
                            </button>
                          </div>

                          <button
                            type="button"
                            className="btn btn-sm btn-success rounded-pill px-3 py-1 fw-bold d-inline-flex align-items-center gap-1 shadow-sm"
                            style={{ fontSize: '11.5px' }}
                            onClick={() => handleComplete(item)}
                            disabled={loadingCompleteId === item._id}
                          >
                            <CheckCircle size={13} />
                            <span>Complete</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* Single / Paged Card View */
                <>
                  {/* Multi-alert horizontal tabs row */}
                  {activeAlerts.length > 1 && (
                    <div className="reminder-tabs-scroll-row">
                      {activeAlerts.map((alt, idx) => {
                        const isSel = idx === safeIndex;
                        const p = getPriorityBadge(alt.priority);
                        return (
                          <button
                            key={alt._id || idx}
                            type="button"
                            className={`reminder-tab-pill ${isSel ? 'active' : ''}`}
                            onClick={() => setCurrentIndex(idx)}
                          >
                            <span className="reminder-tab-index">{idx + 1}</span>
                            <span className="reminder-tab-title">{alt.title || 'Reminder'}</span>
                            <span className="reminder-tab-dot" style={{ backgroundColor: p.iconColor }} />
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Hero Title & Description Card */}
                  <div
                    className="reminder-hero-card"
                    style={{ borderLeft: `5px solid ${pConfig.iconColor}` }}
                  >
                    {/* Meta Top: Priority pill + Type pill + Creator */}
                    <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-1.5">
                      <div className="d-flex align-items-center gap-1.5">
                        <span
                          className="badge rounded-pill px-2.5 py-1 d-inline-flex align-items-center gap-1"
                          style={{
                            backgroundColor: pConfig.bg,
                            color: pConfig.color,
                            border: `1px solid ${pConfig.border}`,
                            fontSize: '11px',
                            fontWeight: '700',
                          }}
                        >
                          <PriorityIcon size={12} />
                          {pConfig.label}
                        </span>

                        {currentAlert.reminderType && (
                          <span
                            className="badge rounded-pill bg-light text-secondary border px-2.5 py-1 d-inline-flex align-items-center gap-1"
                            style={{ fontSize: '11px', fontWeight: '500' }}
                          >
                            <Tag size={10} className="text-muted" />
                            {currentAlert.reminderType}
                          </span>
                        )}
                      </div>

                      {currentAlert.createdBy?.Name && (
                        <span className="text-muted d-flex align-items-center gap-1" style={{ fontSize: '11.5px' }}>
                          <User size={12} className="text-secondary" />
                          <span>By <strong className="text-dark">{currentAlert.createdBy.Name}</strong></span>
                        </span>
                      )}
                    </div>

                    {/* Big Bold Clean Title */}
                    <h4 className="reminder-hero-title">
                      {currentAlert.title}
                    </h4>

                    {/* Structured Notes / Instructions Box */}
                    {currentAlert.description ? (
                      <div className="reminder-note-box">
                        <div className="reminder-note-label">
                          <FileText size={12} className="text-primary" />
                          <span>Notes & Instructions</span>
                        </div>
                        <p className="reminder-note-text">
                          {currentAlert.description}
                        </p>
                      </div>
                    ) : null}
                  </div>

                  {/* Interactive Dual Widget Grid (Scheduled Time + Token/Context) */}
                  <div className="row g-2.5 mt-2">
                    {/* Scheduled Time Widget */}
                    <div className="col-12 col-sm-6">
                      <div className="reminder-widget-card">
                        <div
                          className="reminder-widget-icon"
                          style={{ backgroundColor: '#eff6ff', color: '#2563eb' }}
                        >
                          <Clock size={20} />
                        </div>
                        <div className="reminder-widget-meta">
                          <div className="reminder-widget-label">
                            <Calendar size={11} className="text-muted" />
                            Scheduled For
                          </div>
                          <div className="reminder-widget-value">
                            {timeData.time}
                          </div>
                          <div className="mt-1">
                            <span
                              className="badge rounded-pill px-2 py-0.5"
                              style={{
                                fontSize: '10.5px',
                                fontWeight: '600',
                                backgroundColor: timeData.date === 'Today' ? '#ecfdf5' : '#f8fafc',
                                color: timeData.date === 'Today' ? '#059669' : '#475569',
                                border: `1px solid ${timeData.date === 'Today' ? '#a7f3d0' : '#e2e8f0'}`,
                              }}
                            >
                              {timeData.date}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Linked Token Widget or Category Context Widget */}
                    <div className="col-12 col-sm-6">
                      {hasCallLink ? (
                        <div className="reminder-widget-card">
                          <div
                            className="reminder-widget-icon"
                            style={{ backgroundColor: '#ecfdf5', color: '#059669' }}
                          >
                            <PhoneCall size={20} />
                          </div>
                          <div className="reminder-widget-meta">
                            <div className="reminder-widget-label">
                              <Tag size={11} className="text-muted" />
                              Linked Token
                            </div>
                            <div className="reminder-widget-value text-success text-truncate">
                              Token #{currentAlert.callNo}
                            </div>
                            <div className="mt-1">
                              <button
                                type="button"
                                className="btn btn-sm p-0 border-0 text-primary fw-semibold d-inline-flex align-items-center gap-1"
                                style={{ fontSize: '11px' }}
                                onClick={() => handleViewCallDetails(currentAlert)}
                                title="View Call Details in Support"
                              >
                                <span>Open Details</span>
                                <ExternalLink size={10} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="reminder-widget-card">
                          <div
                            className="reminder-widget-icon"
                            style={{ backgroundColor: pConfig.bg, color: pConfig.color }}
                          >
                            <Sparkles size={20} />
                          </div>
                          <div className="reminder-widget-meta">
                            <div className="reminder-widget-label">
                              <Tag size={11} className="text-muted" />
                              Category
                            </div>
                            <div className="reminder-widget-value text-truncate">
                              {currentAlert.reminderType || 'Call Follow-up'}
                            </div>
                            <div className="mt-1">
                              <span
                                className="badge rounded-pill px-2 py-0.5"
                                style={{
                                  fontSize: '10.5px',
                                  backgroundColor: pConfig.bg,
                                  color: pConfig.color,
                                  border: `1px solid ${pConfig.border}`,
                                }}
                              >
                                {pConfig.label}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quick Snooze Chips Row */}
                  <div className="reminder-quick-snooze-row">
                    <span className="reminder-quick-snooze-label">
                      <Clock size={12} className="text-warning-emphasis" />
                      Quick Snooze:
                    </span>
                    {[
                      { label: '+5m', mins: 5 },
                      { label: '+10m', mins: 10 },
                      { label: '+15m', mins: 15 },
                      { label: '+30m', mins: 30 },
                      { label: '+1h', mins: 60 },
                    ].map((preset) => (
                      <button
                        key={preset.mins}
                        type="button"
                        className="reminder-snooze-chip"
                        disabled={loadingSnoozeMinutes !== null}
                        onClick={() => handleQuickSnooze(currentAlert, preset.mins)}
                        title={`Snooze for ${preset.label}`}
                      >
                        {loadingSnoozeMinutes === preset.mins ? (
                          <span
                            className="spinner-border spinner-border-sm text-warning"
                            role="status"
                            style={{ width: '10px', height: '10px' }}
                          />
                        ) : (
                          preset.label
                        )}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="reminder-snooze-chip ms-auto"
                      onClick={() => handleOpenSnooze(currentAlert)}
                      style={{
                        color: '#2563eb',
                        borderColor: '#bfdbfe',
                        backgroundColor: '#eff6ff',
                      }}
                      title="Choose custom snooze date and time"
                    >
                      Custom...
                    </button>
                  </div>

                  {/* Pagination Dots for Multiple Alerts */}
                  {activeAlerts.length > 1 && (
                    <div className="d-flex align-items-center justify-content-center gap-1.5 mt-2.5 pt-1">
                      {activeAlerts.map((alert, idx) => (
                        <button
                          key={alert._id || idx}
                          type="button"
                          onClick={() => setCurrentIndex(idx)}
                          className="border-0 p-0 rounded-pill"
                          style={{
                            width: idx === safeIndex ? '22px' : '6px',
                            height: '6px',
                            backgroundColor: idx === safeIndex ? pConfig.iconColor : '#cbd5e1',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer',
                          }}
                          title={`Go to reminder ${idx + 1}: ${alert.title}`}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal Action Footer */}
            <div className="reminder-alert-footer">
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="reminder-btn-dismiss"
                  onClick={() => handleDismiss(currentAlert)}
                  title="Dismiss this reminder"
                >
                  <X size={15} />
                  <span>Dismiss</span>
                </button>

                {activeAlerts.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary rounded-pill px-2.5 py-1 d-inline-flex align-items-center gap-1"
                    style={{ fontSize: '11.5px', borderColor: '#cbd5e1', color: '#64748b' }}
                    onClick={handleDismissAll}
                    title="Dismiss all reminders"
                  >
                    <span>Dismiss All ({activeAlerts.length})</span>
                  </button>
                )}
              </div>

              <div className="d-flex align-items-center gap-2">
                {/* Snooze Custom Button */}
                <button
                  type="button"
                  className="reminder-btn-snooze-main"
                  onClick={() => handleOpenSnooze(currentAlert)}
                  title="Choose snooze duration"
                >
                  <Clock size={15} />
                  <span>Snooze</span>
                </button>

                {/* Complete Button */}
                <button
                  type="button"
                  className="reminder-btn-complete-main"
                  onClick={() => handleComplete(currentAlert)}
                  disabled={loadingCompleteId === currentAlert._id}
                  title="Mark this reminder as completed"
                >
                  {loadingCompleteId === currentAlert._id ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      <span>Completing...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      <span>Complete</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Snooze Sub-Modal */}
      {showSnoozeModal && (
        <SnoozeModal
          reminder={snoozeReminderItem}
          show={showSnoozeModal}
          onHide={() => setShowSnoozeModal(false)}
          onSuccess={() => {
            setShowSnoozeModal(false);
          }}
        />
      )}
    </>
  );
};

export default ReminderAlertModal;
